/**
 * IndexedDB Core Module
 * 
 * Generic database operations and connection management for SparkOS.
 * All domain-specific data services should use these primitives.
 */

import { LOCAL_STORAGE_KEYS as LS } from '../../constants';

// --- Database Configuration ---
export const DB_NAME = 'SparkDB';
export const DB_VERSION = 4; // ✅ PERF: Incremented to add indexes for faster queries
export const OBJECT_STORES = Object.values(LS);

// --- Database Connection State ---
let dbPromise: Promise<IDBDatabase> | null = null;
let dbInstance: IDBDatabase | null = null;

// --- Retry Utility ---

/**
 * ✅ PERF: Request deduplication map to prevent duplicate sync operations.
 * Key = operation name, Value = Promise of in-flight operation.
 */
const pendingOperations = new Map<string, Promise<void>>();

/**
 * Retry utility for async operations with exponential backoff.
 */
export const withRetry = async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delayMs: number = 100
): Promise<T> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error as Error;
            console.warn(`Operation failed (attempt ${attempt + 1}/${maxRetries}):`, error);

            if (attempt < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, attempt)));
            }
        }
    }

    throw lastError;
};

/**
 * ✅ PERF: Sync operation wrapper with deduplication, retry, and error logging.
 * - Deduplication: If an operation with the same name is already in-flight, returns immediately
 * - Retry: Automatically retries failed operations with exponential backoff
 * - Logging: Logs errors for debugging
 */
export const syncWithRetry = (
    operation: () => Promise<void>,
    operationName: string
): void => {
    // ✅ PERF: Check if this operation is already in-flight (deduplication)
    if (pendingOperations.has(operationName)) {
        console.debug(`[syncWithRetry] Skipping duplicate operation: ${operationName}`);
        return;
    }

    // Create the promise and track it
    const operationPromise = withRetry(operation, 3, 500)
        .catch(error => {
            console.error(`Cloud sync failed after retries (${operationName}):`, error);
        })
        .finally(() => {
            // ✅ PERF: Remove from pending map when done
            pendingOperations.delete(operationName);
        });

    pendingOperations.set(operationName, operationPromise);
};

/**
 * ✅ PERF: Check if a sync operation is currently pending.
 * Useful for UI to show "syncing" state.
 */
export const isSyncPending = (operationName: string): boolean => {
    return pendingOperations.has(operationName);
};

/**
 * ✅ PERF: Get count of all pending sync operations.
 * Useful for debugging and monitoring.
 */
export const getPendingSyncCount = (): number => {
    return pendingOperations.size;
};

// --- Database Connection ---

/**
 * ✅ PERF: Add indexes to a new store based on its name and usage patterns.
 * Indexes allow faster filtering and sorting instead of full table scans.
 * Note: Defined at module level (not inside callback) to ensure availability.
 */
const addStoreIndexes = (
    _db: IDBDatabase,
    storeName: string,
    store: IDBObjectStore
): void => {
    switch (storeName) {
        case LS.PERSONAL_ITEMS:
            store.createIndex('type', 'type', { unique: false });
            store.createIndex('spaceId', 'spaceId', { unique: false });
            store.createIndex('createdAt', 'createdAt', { unique: false });
            store.createIndex('isCompleted', 'isCompleted', { unique: false });
            store.createIndex('googleTaskId', 'googleTaskId', { unique: false });
            break;

        case LS.FEED_ITEMS:
            store.createIndex('feedId', 'feedId', { unique: false });
            store.createIndex('createdAt', 'createdAt', { unique: false });
            store.createIndex('isRead', 'isRead', { unique: false });
            break;

        case LS.BODY_WEIGHT:
            store.createIndex('date', 'date', { unique: false });
            break;

        case LS.WORKOUT_SESSIONS:
            store.createIndex('startTime', 'startTime', { unique: false });
            break;

        case LS.PERSONAL_EXERCISES:
            store.createIndex('muscleGroup', 'muscleGroup', { unique: false });
            break;

        case LS.SPACES:
            store.createIndex('order', 'order', { unique: false });
            break;

        case LS.GOOGLE_CALENDAR_STORE:
            store.createIndex('start', 'start.dateTime', { unique: false });
            break;

        default:
            break;
    }
};

/**
 * ✅ PERF: Add missing indexes to existing stores during DB upgrade.
 * Only creates indexes that don't already exist.
 */
const addMissingIndexes = (
    _db: IDBDatabase,
    storeName: string,
    store: IDBObjectStore
): void => {
    switch (storeName) {
        case LS.PERSONAL_ITEMS:
            if (!store.indexNames.contains('type')) {
                store.createIndex('type', 'type', { unique: false });
            }
            if (!store.indexNames.contains('spaceId')) {
                store.createIndex('spaceId', 'spaceId', { unique: false });
            }
            if (!store.indexNames.contains('createdAt')) {
                store.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!store.indexNames.contains('isCompleted')) {
                store.createIndex('isCompleted', 'isCompleted', { unique: false });
            }
            if (!store.indexNames.contains('googleTaskId')) {
                store.createIndex('googleTaskId', 'googleTaskId', { unique: false });
            }
            break;

        case LS.FEED_ITEMS:
            if (!store.indexNames.contains('feedId')) {
                store.createIndex('feedId', 'feedId', { unique: false });
            }
            if (!store.indexNames.contains('createdAt')) {
                store.createIndex('createdAt', 'createdAt', { unique: false });
            }
            if (!store.indexNames.contains('isRead')) {
                store.createIndex('isRead', 'isRead', { unique: false });
            }
            break;

        case LS.BODY_WEIGHT:
            if (!store.indexNames.contains('date')) {
                store.createIndex('date', 'date', { unique: false });
            }
            break;

        case LS.WORKOUT_SESSIONS:
            if (!store.indexNames.contains('startTime')) {
                store.createIndex('startTime', 'startTime', { unique: false });
            }
            break;

        case LS.PERSONAL_EXERCISES:
            if (!store.indexNames.contains('muscleGroup')) {
                store.createIndex('muscleGroup', 'muscleGroup', { unique: false });
            }
            break;

        case LS.SPACES:
            if (!store.indexNames.contains('order')) {
                store.createIndex('order', 'order', { unique: false });
            }
            break;

        default:
            break;
    }
};

/**
 * Initializes and returns a memoized connection to the IndexedDB database.
 * Includes connection recovery and better error handling.
 */
export const initDB = (): Promise<IDBDatabase> => {
    // Return existing connection if available and open
    if (dbInstance && dbInstance.objectStoreNames.length > 0) {
        return Promise.resolve(dbInstance);
    }

    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        // Check if IndexedDB is available
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB is not supported in this environment'));
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('IndexedDB error:', request.error);
            dbPromise = null; // Reset so next call will retry
            reject(new Error(`Error opening IndexedDB: ${request.error?.message || 'Unknown error'}`));
        };

        request.onblocked = () => {
            console.warn('IndexedDB upgrade blocked - close other tabs');
        };

        request.onupgradeneeded = event => {
            const db = (event.target as IDBOpenDBRequest).result;

            // Handle database errors during upgrade
            db.onerror = e => {
                console.error('Database error during upgrade:', e);
            };

            OBJECT_STORES.forEach(storeName => {
                if (!db.objectStoreNames.contains(storeName)) {
                    try {
                        const keyPath = storeName === LS.AUTH_TOKENS ? 'service' : 'id';
                        const store = db.createObjectStore(storeName, { keyPath });

                        // ✅ PERF: Add indexes for commonly queried fields to speed up filtering/sorting
                        addStoreIndexes(db, storeName, store);
                    } catch (e) {
                        console.error(`Failed to create store ${storeName}:`, e);
                    }
                } else {
                    // ✅ PERF: Add indexes to existing stores if they don't have them (for DB upgrades)
                    try {
                        // Note: In onupgradeneeded, we use db.transaction() to get the store
                        const tx = db.transaction(storeName);
                        const store = tx.objectStore(storeName);
                        addMissingIndexes(db, storeName, store);
                    } catch (e) {
                        console.error(`Failed to add indexes to ${storeName}:`, e);
                    }
                }
            });
        };

        request.onsuccess = () => {
            dbInstance = request.result;

            // Handle connection close to allow reconnection
            dbInstance.onclose = () => {
                console.warn('IndexedDB connection closed, will reconnect on next operation');
                dbInstance = null;
                dbPromise = null;
            };

            // Handle version change (another tab upgraded the DB)
            dbInstance.onversionchange = () => {
                dbInstance?.close();
                dbInstance = null;
                dbPromise = null;
                console.warn('Database version changed - please reload the page');
            };

            resolve(dbInstance);
        };
    });
    return dbPromise;
};

/**
 * Gets an object store from the database within a transaction.
 */
export const getStore = async (storeName: string, mode: IDBTransactionMode): Promise<IDBObjectStore> => {
    const db = await initDB();
    return db.transaction(storeName, mode).objectStore(storeName);
};

// --- Generic CRUD Operations ---

/**
 * Get all items from a store.
 */
export const dbGetAll = async <T>(storeName: string): Promise<T[]> => {
    return withRetry(async () => {
        const store = await getStore(storeName, 'readonly');
        return new Promise((resolve, reject) => {
            const request: IDBRequest<T[]> = store.getAll();
            request.onerror = () =>
                reject(new Error(`Failed to get all from ${storeName}: ${request.error?.message}`));
            request.onsuccess = () => resolve(request.result || []);
        });
    });
};

/**
 * Get a single item from a store by key.
 */
export const dbGet = async <T>(storeName: string, key: IDBValidKey): Promise<T | undefined> => {
    return withRetry(async () => {
        const store = await getStore(storeName, 'readonly');
        return new Promise((resolve, reject) => {
            const request: IDBRequest<T> = store.get(key);
            request.onerror = () =>
                reject(new Error(`Failed to get ${key} from ${storeName}: ${request.error?.message}`));
            request.onsuccess = () => resolve(request.result);
        });
    });
};

/**
 * Put (upsert) an item into a store.
 */
export const dbPut = async <T>(storeName: string, item: T): Promise<void> => {
    return withRetry(async () => {
        const store = await getStore(storeName, 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.put(item);
            request.onerror = () =>
                reject(new Error(`Failed to put item in ${storeName}: ${request.error?.message}`));
            request.onsuccess = () => resolve();
        });
    });
};

/**
 * Delete an item from a store by key.
 */
export const dbDelete = async (storeName: string, key: IDBValidKey): Promise<void> => {
    return withRetry(async () => {
        const store = await getStore(storeName, 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.delete(key);
            request.onerror = () =>
                reject(new Error(`Failed to delete ${key} from ${storeName}: ${request.error?.message}`));
            request.onsuccess = () => resolve();
        });
    });
};

/**
 * Clear all items from a store.
 */
export const dbClear = async (storeName: string): Promise<void> => {
    return withRetry(async () => {
        const store = await getStore(storeName, 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onerror = () =>
                reject(new Error(`Failed to clear ${storeName}: ${request.error?.message}`));
            request.onsuccess = () => resolve();
        });
    }, 2); // Only 2 retries for clear operation
};

/**
 * ✅ PERF: Put multiple items in a single transaction (batch operation).
 * Much more efficient than multiple individual puts - reduces transaction overhead.
 * 
 * @example
 * await dbPutBatch('personalItems', [item1, item2, item3]);
 */
export const dbPutBatch = async <T>(storeName: string, items: T[]): Promise<void> => {
    if (items.length === 0) return;

    return withRetry(async () => {
        const db = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            items.forEach(item => store.put(item));

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(new Error(`Failed to batch put in ${storeName}: ${transaction.error?.message}`));
        });
    });
};

/**
 * ✅ PERF: Delete multiple items in a single transaction (batch operation).
 * Much more efficient than multiple individual deletes.
 */
export const dbDeleteBatch = async (storeName: string, keys: IDBValidKey[]): Promise<void> => {
    if (keys.length === 0) return;

    return withRetry(async () => {
        const db = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            keys.forEach(key => store.delete(key));

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(new Error(`Failed to batch delete from ${storeName}: ${transaction.error?.message}`));
        });
    });
};

// --- Utility Functions ---

/**
 * Initializes a data store with default data if it's empty.
 * @deprecated Use initializeDefaultDataLazy for better bundle size
 */
export const initializeDefaultData = async <T>(storeName: string, defaultData: T[]): Promise<T[]> => {
    const data = await dbGetAll<T>(storeName);
    if (data.length === 0 && defaultData.length > 0) {
        const store = await getStore(storeName, 'readwrite');
        const transaction = store.transaction;

        defaultData.forEach(item => store.put(item));

        return new Promise((resolve, reject) => {
            transaction.oncomplete = () => resolve(defaultData);
            transaction.onerror = () => reject(transaction.error);
        });
    }
    return data;
};

/**
 * Initializes a data store with lazy-loaded default data if it's empty.
 * Default data is fetched only when the store is empty (first install).
 * This reduces bundle size by not importing default data at build time.
 */
export const initializeDefaultDataLazy = async <T>(
    storeName: string,
    getDefaultData: () => Promise<T[]>
): Promise<T[]> => {
    const data = await dbGetAll<T>(storeName);

    // Only fetch defaults if store is empty
    if (data.length === 0) {
        try {
            const defaultData = await getDefaultData();
            if (defaultData.length > 0) {
                const store = await getStore(storeName, 'readwrite');
                const transaction = store.transaction;

                defaultData.forEach(item => store.put(item));

                return new Promise((resolve, reject) => {
                    transaction.oncomplete = () => resolve(defaultData);
                    transaction.onerror = () => reject(transaction.error);
                });
            }
        } catch (error) {
            console.warn(`[initializeDefaultDataLazy] Failed to load defaults for ${storeName}:`, error);
        }
    }

    return data;
};

/**
 * Safe date sorting utility for items with createdAt field.
 */
export const safeDateSort = (a: { createdAt: string }, b: { createdAt: string }): number => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (isNaN(dateB)) return -1;
    if (isNaN(dateA)) return 1;
    return dateB - dateA;
};
